// The dimension taxonomy the report groups by: ids, display labels and the
// four picker categories (ticket §5.3).
//
// This is CONFIG, not data. Labels here are the synchronous fallback used
// before `useAnalyticsMeta` resolves and by the pure modules that can't hold a
// query (the public render route, the PDF payload builder). The server's
// registry is the source of truth once loaded — see `useDimensionCatalog`,
// which overlays live labels and drops dimensions the backend reports as
// unavailable.
//
// Replaces the former `mock-data.ts`, whose per-dimension `values[]` arrays of
// invented sample rows ("Acme Lead Gen", "website-1.com") are gone: every
// surface that once read them now reads live rows or the per-row `iconMeta`.
import type { DimensionCategory, DimensionDef, DimensionId } from '../types'

export const DIMENSIONS: Record<DimensionId, DimensionDef> = {
	country: { id: 'country', label: 'Country' },
	region: { id: 'region', label: 'Region' },
	city: { id: 'city', label: 'City' },
	ad_platform: { id: 'ad_platform', label: 'Ad platform' },
	ad_account: { id: 'ad_account', label: 'Ad account' },
	campaign_type: { id: 'campaign_type', label: 'Campaign type' },
	campaign: { id: 'campaign', label: 'Campaign' },
	ad_group: { id: 'ad_group', label: 'Ad group / Ad set' },
	keyword: { id: 'keyword', label: 'Keyword' },
	isp: { id: 'isp', label: 'ISP' },
	ip_usage: { id: 'ip_usage', label: 'IP usage' },
	device_type: { id: 'device_type', label: 'Device type' },
	os: { id: 'os', label: 'Operating system' },
	browser: { id: 'browser', label: 'Browser' },
	placement: { id: 'placement', label: 'Placement' },
	target_url: { id: 'target_url', label: 'Target URL' },
	// Referrer is intentionally absent: it's never captured, so the backend
	// registry doesn't define it and every query using it 400s. Dropped with PO
	// sign-off (supersedes the §4.5/§5.3 listing). See useDimensionCatalog, which
	// also hides any dimension the server doesn't return as a safety net.
	status: { id: 'status', label: 'Status' },
	utm_source: { id: 'utm_source', label: 'utm_source' },
	utm_campaign: { id: 'utm_campaign', label: 'utm_campaign' },
	utm_content: { id: 'utm_content', label: 'utm_content' },
	utm_medium: { id: 'utm_medium', label: 'utm_medium' },
	utm_term: { id: 'utm_term', label: 'utm_term' }
}

export const DIMENSION_CATEGORIES: DimensionCategory[] = [
	{ id: 'geography', label: 'Geography', dimensionIds: ['country', 'region', 'city'] },
	{
		id: 'channel',
		label: 'Channel',
		dimensionIds: ['ad_platform', 'ad_account', 'campaign_type', 'campaign', 'ad_group', 'keyword']
	},
	{
		id: 'origin',
		label: 'Origin',
		dimensionIds: ['isp', 'ip_usage', 'device_type', 'os', 'browser', 'placement', 'target_url', 'status']
	},
	{
		id: 'tracking',
		label: 'Tracking',
		dimensionIds: ['utm_source', 'utm_campaign', 'utm_content', 'utm_medium', 'utm_term']
	}
]

/** Ticket §5.2: Dimension 1 is required and defaults to Country. */
export const DEFAULT_PRIMARY_DIMENSION: DimensionId = 'country'
